import * as React from 'react';
import Recorder, { RecorderEvent, RecordingState } from './Recorder';
import { Overlay } from '../components/Overlay';
import { ProgressBar } from '../components/ProgressBar';
import { storeDispatch, getStoreState } from '../state/Store';
import * as Actions from '../state/actions/index';
import { trackEvent } from 'utils/Tracker';
import { PlatformType } from 'data/PlatformType';
import { isMobileLayout, pause } from 'utils';

interface Props {
  recorder: Recorder;
}

interface State {
  rState: RecordingState;
  progress: number | undefined;
  details: string;
}

const cancelRecording = () => {
  storeDispatch(Actions.cancelRecording());
};

class RecordingModal extends React.Component<Props, State> {
  public state: State = {
    rState: RecordingState.Recording,
    progress: 0,
    details: 'Calculating...'
  };
  private recordingTimeInt: number;
  private lastProgress: number = 0;
  private lastProgressTime: number;

  componentDidMount() {
    this.recordingTimeInt = window.setInterval(this.update, 500);
    const { recorder } = this.props;
    recorder.on(RecorderEvent.UploadProgressed, this.uploadProgressed);
    recorder.on(RecorderEvent.ConversionProgressed, this.conversionProgressed);
    this.lastProgressTime = Date.now();
  }
  componentWillUnmount() {
    clearInterval(this.recordingTimeInt);
    const { recorder } = this.props;
    recorder.off(RecorderEvent.UploadProgressed, this.uploadProgressed);
    recorder.off(RecorderEvent.ConversionProgressed, this.conversionProgressed);
  }

  uploadProgressed = (progress: number) => {
    this.setState({
      rState: RecordingState.Uploading,
      progress,
      details: this.getProgressDetails(progress)
    });
  };

  conversionProgressed = (progress: number | undefined) => {
    this.setState({
      rState: RecordingState.Converting,
      progress,
      details: this.getProgressDetails(progress)
    });
  };

  getProgressDetails = (progress: number | undefined): string => {
    if (progress === undefined) {
      return '';
    }
    const now = Date.now();
    if (progress <= this.lastProgress) {
      this.lastProgress = progress;
      this.lastProgressTime = now;
      return '';
    }

    const timeDiffSeconds = (now - this.lastProgressTime) / 1000;
    if (timeDiffSeconds < 0.5) {
      return this.state.details;
    }
    const speed = (progress - this.lastProgress) / timeDiffSeconds; // speed in seconds
    let seconds = Math.ceil((1 - progress) / speed);
    this.lastProgress = progress;
    this.lastProgressTime = now;
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'} remaining...`;
    } else {
      return `${seconds} second${seconds === 1 ? '' : 's'} remaining...`;
    }
  };

  update = () => {
    const progress = this.props.recorder.getPosition();
    const details = this.getProgressDetails(progress);
    this.setState({ progress, details });
    if (progress >= 1) {
      clearInterval(this.recordingTimeInt);
    }
  };

  didClickDesktopApp = () => {
    trackEvent('Get Desktop App While Recording');
    window.open('https://beatflyer.com/download', '_blank');
  };

  didFigmaUpgrade = async () => {
    storeDispatch(Actions.cancelRecording());
    pause(1);
    storeDispatch(Actions.showBuyFigma('recording quality'));
  };

  render() {
    const { rState, progress, details } = this.state;
    const { platform, figmaPaymentStatus } = getStoreState().app;
    let addition: React.ReactElement | null = null;
    if (rState === RecordingState.Recording) {
      if (platform === PlatformType.Web) {
        addition = <span> keep this tab visible.</span>;
      } else if (platform === PlatformType.Desktop) {
        addition = <span> to work on a new project open a new window.</span>;
      }
    } else {
      if (platform === PlatformType.Web && !isMobileLayout()) {
        addition = (
          <span>
            {' '}
            use the{' '}
            <a rel="noopener noreferrer" onClick={this.didClickDesktopApp}>
              desktop app
            </a>{' '}
            to skip this step
          </span>
        );
      }
    }
    return (
      <Overlay title={rState} hideCloseButton={true}>
        <ProgressBar level={progress} />
        <div className="recording-metadata">
          {details}
          {addition}
          {platform === PlatformType.iOSApp &&
            rState === RecordingState.Recording && <> Keep the app visible.</>}
          {platform === PlatformType.Web &&
            !isMobileLayout() &&
            rState === RecordingState.Recording && (
              <>
                <br />
                <span>
                  Use the{' '}
                  <a
                    rel="noopener noreferrer"
                    onClick={this.didClickDesktopApp}
                  >
                    desktop app
                  </a>{' '}
                  to record in the background.
                </span>
              </>
            )}
          {platform === PlatformType.Figma && figmaPaymentStatus !== 'PAID' && (
            <>
              {' '}
              <a rel="noopener noreferrer" onClick={this.didFigmaUpgrade}>
                Upgrade
              </a>{' '}
              for better video quality.
            </>
          )}
        </div>
        <button className="large stop-recording" onClick={cancelRecording}>
          Cancel
        </button>
      </Overlay>
    );
  }
}

export { RecordingModal };



// WEBPACK FOOTER //
// ./src/video/RecordingModal.tsx